import { useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ChatMessage } from "@/components/chat/ChatMessage"
import { ChatImageBurstMessage } from '@/components/chat/ChatImageBurstMessage'
import type { Message, MessageReplyTo } from '@/types/models'
import {
  canAppendMessageToImageBurst,
  getMessageTimestampAsDate,
  isBareImageBurstPartMessage,
} from '@/lib/chatImageBurst'
import { WelcomeMessageBanner } from "@/components/chat/WelcomeMessageBanner"
import { getUserId } from "@/lib/chatData"
import type { Chat, Artist, ChatSettings } from "@/types/models"
import type { ChatUserInfo } from "@/lib/chatData"
import type { CachedChatDisplayData } from "@/lib/chatListCache"
import { formatArtistDisplayName } from '@/lib/utils'
import { format, isToday, isYesterday } from 'date-fns'
import { StickToBottom } from 'use-stick-to-bottom'
import { ChatsIcon } from '@/lib/navigationItems'
import { useTheme } from '@/contexts/ThemeContext'
import logoDark from '@/assets/images/logo_no_bg.png'
import logoLight from '@/assets/images/logo_no_bg_white.png'

function getClientSenderName(chatUser: ChatUserInfo | null | undefined): string | undefined {
  if (!chatUser) return undefined
  if (chatUser.instagram) {
    return chatUser.instagram.startsWith('@') ? chatUser.instagram : `@${chatUser.instagram.replace(/^@+/, '')}`
  }
  if (chatUser.displayName) return chatUser.displayName
  if (chatUser.email) return chatUser.email
  return undefined
}

interface ChatMessagesContainerProps {
  isNewChat: boolean
  isNewChatCreated: boolean
  chatLoading: boolean
  chat: Chat | null
  artist: Artist | null
  chatUser: ChatUserInfo | null
  onBookingCreated?: () => void
  chatSettings?: ChatSettings | null
  welcomeMessage?: { content: string } | null
  keyboardHeight?: number
  otherPartyIsTyping?: boolean
  isArtist?: boolean
  flashId?: string | null
  cachedDisplayData?: CachedChatDisplayData | null
  onRetryMessage?: (clientGeneratedId: string) => void
  onAddToTimeline?: (imageUrl: string) => void
  onReplyToMessage?: (message: Message) => void
  onReplyToImageBurst?: (messages: Message[]) => void
  getReplyQuoteAuthorLabel?: (reply: MessageReplyTo) => string
}

export const ChatMessagesContainer = function ChatMessagesContainer({
  isNewChat,
  isNewChatCreated,
  chatLoading,
  chat,
  artist,
  chatUser,
  onBookingCreated,
  chatSettings,
  welcomeMessage,
  keyboardHeight = 0,
  otherPartyIsTyping = false,
  isArtist = false,
  cachedDisplayData,
  onRetryMessage,
  onAddToTimeline,
  onReplyToMessage,
  onReplyToImageBurst,
  getReplyQuoteAuthorLabel,
}: ChatMessagesContainerProps) {
  const { t } = useTranslation()
  const { isDarkMode } = useTheme()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const previousMessageCountRef = useRef(0)
  
  // Get assistant avatar based on theme
  const assistantAvatar = isDarkMode ? logoLight : logoDark

  // Function to scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  // Scroll to bottom when NEW messages are added or typing indicator appears
  // But NOT when existing messages are just updated (like toggled)
  useEffect(() => {
    if (!messagesEndRef.current) return

    const currentMessageCount = chat?.messages?.length || 0
    const messageCountChanged = currentMessageCount !== previousMessageCountRef.current
    
    // Only scroll if message count changed or typing indicators changed
    if (messageCountChanged) {
      previousMessageCountRef.current = currentMessageCount
      
      // Initial scroll with delay to let DOM settle
      const initialScrollTimeout = setTimeout(() => {
        scrollToBottom()
      }, 100)

      // Setup ResizeObserver to detect when images load and container size changes
      const resizeObserver = new ResizeObserver(() => {
        scrollToBottom()
      })

      if (contentRef.current) {
        resizeObserver.observe(contentRef.current)
      }

      return () => {
        clearTimeout(initialScrollTimeout)
        resizeObserver.disconnect()
      }
    }
  }, [chat?.messages?.length, scrollToBottom])
  
  // Scroll to bottom when keyboard height changes
  useEffect(() => {
    if (keyboardHeight > 0) {
      // Small delay to let margin adjustment take effect
      setTimeout(() => {
        scrollToBottom()
      }, 100)
    }
  }, [keyboardHeight, scrollToBottom])

  if (isNewChat && !isNewChatCreated) {
    // Show welcome message if artist has one configured
    const shouldShowWelcome = chatSettings?.mode === 'welcome_message' && welcomeMessage?.content
    
    if (shouldShowWelcome) {
      return (
        <div className="min-h-full pt-4 pb-0 phone-container-padded flex flex-col bg-background">
          <StickToBottom.Content className="flex flex-col">
            <WelcomeMessageBanner
              content={welcomeMessage.content}
              artistAvatar={artist?.avatarUrl}
              className="mb-4"
            />
          </StickToBottom.Content>
        </div>
      )
    }
    
    // Show default prompt for new chat
    return (
      <div className="h-full flex items-center justify-center bg-background phone-container-padded pt-16">
        <div className="text-center p-6 max-w-sm flex flex-col items-center">
          <div className="mb-6">
            <ChatsIcon className="w-12 h-12 text-foreground" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">
            {t('chat.newChatTitle')}
          </h3>
          <p className="text-muted-foreground leading-relaxed">
            {t('chat.newChatDescription')}
          </p>
        </div>
      </div>
    )
  }

  if (chatLoading && !chat) {
    return (
      <div className="min-h-full bg-background phone-container-padded" aria-hidden />
    )
  }

  if (!chat) {
    return (
      <div className="h-full flex items-center justify-center bg-background phone-container-padded">
        <div className="text-center p-6 max-w-sm">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4 mx-auto">
            <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">
            {t('chat.chatNotFound')}
          </h3>
          <p className="text-muted-foreground">
            {t('chat.chatNotFoundDescription')}
          </p>
        </div>
      </div>
    )
  }

  return (
      <div 
        className="min-h-full pt-4 pb-0 phone-container-padded flex flex-col bg-background"
        onClick={(e) => {
          // This prevents event bubbling from the selector which would otherwise affect page scroll
          e.stopPropagation()
        }}
      >
        <div ref={contentRef}>
          <StickToBottom.Content className="flex flex-col">
        {(() => {
          // Build interleaved list with time separators for gaps > 5 minutes
          const items: Array<
            | { type: 'separator'; key: string; label: string }
            | {
                type: 'message'
                key: string
                message: Message
                isFirstInGroup: boolean
                isLastInGroup: boolean
              }
            | {
                type: 'imageBurst'
                key: string
                messages: Message[]
                isFirstInGroup: boolean
                isLastInGroup: boolean
              }
          > = []
          const FIVE_MIN_MS = 5 * 60 * 1000
          const now = new Date()

          const formatSeparator = (date: Date) => {
            if (isToday(date)) {
              return format(date, 'HH:mm')
            }
            if (isYesterday(date)) {
              return `YESTERDAY AT ${format(date, 'HH:mm')}`
            }
            const diffDays = Math.floor((now.getTime() - date.getTime()) / (24 * 60 * 60 * 1000))
            if (diffDays < 7) {
              const day = format(date, 'EEE').toUpperCase()
              return `${day} AT ${format(date, 'HH:mm')}`
            }
            return `${format(date, 'd MMM').toUpperCase()} AT ${format(date, 'HH:mm')}`
          }

          let previousDate: Date | null = null
          let previousSenderId: string | null = null
          let previousSenderType: string | null = null
          let scanIndex = 0
          while (scanIndex < chat.messages.length) {
            const currentMessageRecord = chat.messages[scanIndex]
            const timestampForCurrentScan = getMessageTimestampAsDate(currentMessageRecord)

            const isFiveMinuteSeparatorGap =
              !previousDate || timestampForCurrentScan.getTime() - previousDate.getTime() > FIVE_MIN_MS
            const isDifferentSenderVersusPriorBubble =
              previousSenderId !== currentMessageRecord.senderId ||
              previousSenderType !== currentMessageRecord.senderType
            const currentMessageIsStructuralGroupBreakerType =
              currentMessageRecord.type === 'booking_selection' ||
              currentMessageRecord.type === 'past_booking' ||
              currentMessageRecord.type === 'notification' ||
              currentMessageRecord.type === 'simple_availability'
            const isFirstBubbleInSenderGroup =
              isFiveMinuteSeparatorGap ||
              isDifferentSenderVersusPriorBubble ||
              currentMessageIsStructuralGroupBreakerType

            let contiguousBareImageBurstMessages: Message[] | null = null
            let indexAdvancePastConsumedMessages = 1
            if (isBareImageBurstPartMessage(currentMessageRecord)) {
              const expandableBareImageRun: Message[] = [currentMessageRecord]
              let lookaheadIndex = scanIndex + 1
              while (
                lookaheadIndex < chat.messages.length &&
                canAppendMessageToImageBurst(
                  chat.messages[lookaheadIndex - 1],
                  chat.messages[lookaheadIndex]
                )
              ) {
                expandableBareImageRun.push(chat.messages[lookaheadIndex])
                lookaheadIndex++
              }
              if (expandableBareImageRun.length >= 2) {
                contiguousBareImageBurstMessages = expandableBareImageRun
                indexAdvancePastConsumedMessages = expandableBareImageRun.length
              }
            }

            const groupingAnchorMessageForBubbleTail =
              contiguousBareImageBurstMessages
                ? contiguousBareImageBurstMessages[contiguousBareImageBurstMessages.length - 1]
                : currentMessageRecord

            const nextMessageAfterThisVisualItem =
              chat.messages[scanIndex + indexAdvancePastConsumedMessages]
            let isLastBubbleInSenderGroupForThisVisualItem = true
            if (nextMessageAfterThisVisualItem) {
              const groupingAnchorTimestamp = getMessageTimestampAsDate(groupingAnchorMessageForBubbleTail)
              const nextMessageTimestamp = getMessageTimestampAsDate(nextMessageAfterThisVisualItem)
              const nextMessageBreaksFiveMinuteSilenceGap =
                nextMessageTimestamp.getTime() - groupingAnchorTimestamp.getTime() > FIVE_MIN_MS
              const nextMessageIsDifferentSenderVersusBurstTail =
                nextMessageAfterThisVisualItem.senderId !== groupingAnchorMessageForBubbleTail.senderId ||
                nextMessageAfterThisVisualItem.senderType !== groupingAnchorMessageForBubbleTail.senderType
              const nextMessageIsStructuralGroupBreakerType =
                nextMessageAfterThisVisualItem.type === 'booking_selection' ||
                nextMessageAfterThisVisualItem.type === 'past_booking' ||
                nextMessageAfterThisVisualItem.type === 'notification' ||
                nextMessageAfterThisVisualItem.type === 'simple_availability'
              isLastBubbleInSenderGroupForThisVisualItem =
                nextMessageBreaksFiveMinuteSilenceGap ||
                nextMessageIsDifferentSenderVersusBurstTail ||
                nextMessageIsStructuralGroupBreakerType
            }

            if (isFiveMinuteSeparatorGap) {
              items.push({
                type: 'separator',
                key: `sep-${currentMessageRecord.id}`,
                label: formatSeparator(timestampForCurrentScan),
              })
            }

            if (contiguousBareImageBurstMessages) {
              items.push({
                type: 'imageBurst',
                key: contiguousBareImageBurstMessages.map(m => m.id).join('-'),
                messages: contiguousBareImageBurstMessages,
                isFirstInGroup: isFirstBubbleInSenderGroup,
                isLastInGroup: isLastBubbleInSenderGroupForThisVisualItem,
              })
            } else {
              items.push({
                type: 'message',
                key: currentMessageRecord.id,
                message: currentMessageRecord,
                isFirstInGroup: isFirstBubbleInSenderGroup,
                isLastInGroup: isLastBubbleInSenderGroupForThisVisualItem,
              })
            }

            previousDate = getMessageTimestampAsDate(groupingAnchorMessageForBubbleTail)
            previousSenderId = groupingAnchorMessageForBubbleTail.senderId
            previousSenderType = groupingAnchorMessageForBubbleTail.senderType
            scanIndex += indexAdvancePastConsumedMessages
          }

          return items.map((item) => {
            if (item.type === 'separator') {
              return (
                <div key={item.key} className="flex justify-center my-3">
                  <div className="text-xs leading-[14px]">
                    {item.label}
                  </div>
                </div>
              )
            }

            if (item.type === 'imageBurst') {
              const lastBurstMessageSnapshot = item.messages[item.messages.length - 1]

              return (
                <ChatImageBurstMessage
                  key={item.key}
                  messages={item.messages}
                  isOwnMessage={lastBurstMessageSnapshot.senderId === getUserId()}
                  isContinuation={!item.isFirstInGroup}
                  isLastInGroup={item.isLastInGroup}
                  isLastInChat={chat.messages[chat.messages.length - 1]?.id === lastBurstMessageSnapshot.id}
                  senderName={
                    lastBurstMessageSnapshot.senderType === 'artist'
                      ? formatArtistDisplayName(artist || undefined) || undefined
                      : lastBurstMessageSnapshot.senderType === 'user'
                        ? getClientSenderName(chatUser)
                        : undefined
                  }
                  senderAvatar={
                    lastBurstMessageSnapshot.senderType === 'artist'
                      ? cachedDisplayData?.avatarUrl || artist?.avatarUrl || undefined
                      : lastBurstMessageSnapshot.senderType === 'user'
                        ? cachedDisplayData?.avatarUrl || chatUser?.avatarUrl || undefined
                        : lastBurstMessageSnapshot.senderType === 'assistant'
                          ? assistantAvatar
                          : undefined
                  }
                  senderFallbackInitial={
                    lastBurstMessageSnapshot.senderType === 'artist'
                      ? cachedDisplayData?.avatarInitial || (artist?.displayName || '?').charAt(0)
                      : lastBurstMessageSnapshot.senderType === 'user'
                        ? cachedDisplayData?.avatarInitial ||
                          (() => {
                            const initialSource =
                              chatUser?.instagram || chatUser?.email || chatUser?.displayName || '?'
                            return (initialSource.startsWith('@') ? initialSource.slice(1) : initialSource).charAt(0)
                          })()
                        : undefined
                  }
                  chatId={chat.id}
                  artistId={chat.artistId}
                  onBookingCreated={onBookingCreated}
                  isArtist={isArtist}
                  onRetryMessage={onRetryMessage}
                  onAddToTimeline={onAddToTimeline}
                  onReplyToImageBurst={onReplyToImageBurst}
                />
              )
            }

            const message = item.message
            
            return (
              <ChatMessage
                key={item.key}
                message={message}
                isOwnMessage={message.senderId === getUserId()}
                isContinuation={item.type === 'message' ? !item.isFirstInGroup : false}
                isLastInGroup={item.type === 'message' ? item.isLastInGroup : true}
                isLastInChat={chat.messages[chat.messages.length - 1]?.id === message.id}
                senderName={
                  message.senderType === 'artist'
                    ? formatArtistDisplayName(artist || undefined) || undefined
                    : message.senderType === 'user'
                    ? getClientSenderName(chatUser)
                    : undefined
                }
                senderAvatar={
                  message.senderType === 'artist'
                    ? (cachedDisplayData?.avatarUrl || artist?.avatarUrl || undefined)
                    : message.senderType === 'user'
                    ? (cachedDisplayData?.avatarUrl || chatUser?.avatarUrl || undefined)
                    : message.senderType === 'assistant'
                    ? assistantAvatar
                    : undefined
                }
                senderFallbackInitial={
                  message.senderType === 'artist'
                    ? (cachedDisplayData?.avatarInitial || (artist?.displayName || '?').charAt(0))
                    : message.senderType === 'user'
                    ? (cachedDisplayData?.avatarInitial || (() => {
                        const initialSource = chatUser?.instagram || chatUser?.email || chatUser?.displayName || '?'
                        return (initialSource.startsWith('@') ? initialSource.slice(1) : initialSource).charAt(0)
                      })())
                    : undefined
                }
                chatId={chat.id}
                artistId={chat.artistId}
                onBookingCreated={onBookingCreated}
                isArtist={isArtist}
                onRetryMessage={onRetryMessage}
                onAddToTimeline={onAddToTimeline}
                onReplyToMessage={onReplyToMessage}
                getReplyQuoteAuthorLabel={getReplyQuoteAuthorLabel}
              />
            )
          })
        })()}
        
        {/* Typing indicator for other party (artist or user) */}
        {otherPartyIsTyping && (
          <div className="flex justify-start mb-1 items-end gap-2 max-w-[70%]">
            {/* Avatar */}
            <div className="w-8 h-8 flex-shrink-0">
              <div className="w-8 h-8 rounded-full overflow-hidden bg-muted flex items-center justify-center text-sm font-medium">
                {isArtist ? (
                  // Artist view: show user avatar
                  (cachedDisplayData?.avatarUrl || chatUser?.avatarUrl) ? (
                    <img 
                      src={cachedDisplayData?.avatarUrl || chatUser?.avatarUrl || ''} 
                      alt="User is typing" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-primary/10 text-primary">
                      {(cachedDisplayData?.avatarInitial || (() => {
                        const initialSource = chatUser?.instagram || chatUser?.email || chatUser?.displayName || '?'
                        return (initialSource.startsWith('@') ? initialSource.slice(1) : initialSource).charAt(0)
                      })()).toUpperCase()}
                    </div>
                  )
                ) : (
                  // User view: show artist avatar
                  (cachedDisplayData?.avatarUrl || artist?.avatarUrl) ? (
                    <img 
                      src={cachedDisplayData?.avatarUrl || artist?.avatarUrl || ''} 
                      alt="Artist is typing" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-primary/10 text-primary">
                      {(cachedDisplayData?.avatarInitial || (artist?.displayName || '?').charAt(0)).toUpperCase()}
                    </div>
                  )
                )}
              </div>
            </div>
            {/* Typing bubble */}
            <div className="bg-muted rounded-[18px] rounded-bl-[8px] px-4 py-3">
              <div className="flex items-center gap-1">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"></div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Invisible element to scroll to with dynamic bottom margin for keyboard */}
        <div 
          ref={messagesEndRef} 
          className="h-[1px]"
          style={{
            marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0',
            transition: 'margin-bottom 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
          }}
        />
        </StickToBottom.Content>
        </div>
      </div>
  )
}
